/**
 * useSimplifyDeveloperPlans hook — orchestrates server-side data fetching,
 * filter mapping, debouncing, and Extend Trial flow for the Simplify tab.
 */

import { useCallback, useEffect, useState } from 'react';
import { logger, mediator } from '@/application/core';
import {
  GetSimplifyDeveloperPlansQuery,
  type SimplifyDeveloperPlansResponse,
  type SimplifyGridParams,
} from '@/application/queries/simplifyDeveloperPlan';
import {
  ExtendSimplifyTrialFECommand,
  type ExtendSimplifyTrialResult,
} from '@/application/commands/simplifyDeveloperPlan';
import { useSimplifyDeveloperPlansStore } from './SimplifyDeveloperPlansTab.store';

const SEARCHABLE_COLUMN = 'companyName';

export function useSimplifyDeveloperPlans() {
  const items = useSimplifyDeveloperPlansStore((s) => s.items);
  const totalCount = useSimplifyDeveloperPlansStore((s) => s.totalCount);
  const loading = useSimplifyDeveloperPlansStore((s) => s.loading);
  const error = useSimplifyDeveloperPlansStore((s) => s.error);
  const pagination = useSimplifyDeveloperPlansStore((s) => s.pagination);
  const sorting = useSimplifyDeveloperPlansStore((s) => s.sorting);
  const searchText = useSimplifyDeveloperPlansStore((s) => s.searchText);

  const setData = useSimplifyDeveloperPlansStore((s) => s.setData);
  const setLoading = useSimplifyDeveloperPlansStore((s) => s.setLoading);
  const setError = useSimplifyDeveloperPlansStore((s) => s.setError);
  const setExtending = useSimplifyDeveloperPlansStore((s) => s.setExtending);
  const setExtendError = useSimplifyDeveloperPlansStore((s) => s.setExtendError);
  const setExtendSuccess = useSimplifyDeveloperPlansStore((s) => s.setExtendSuccess);
  const updateItemTrialDate = useSimplifyDeveloperPlansStore((s) => s.updateItemTrialDate);

  // Debounce search text 300ms before triggering refetch.
  const [debouncedSearch, setDebouncedSearch] = useState(searchText);
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchText), 300);
    return () => clearTimeout(handle);
  }, [searchText]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const search = debouncedSearch.trim();
      const params: SimplifyGridParams = {
        page: pagination.page,
        pageSize: pagination.pageSize,
        orders: sorting.map((s) => ({ id: s.id, desc: s.desc })),
        filters: search ? [{ id: SEARCHABLE_COLUMN, values: [search] }] : [],
      };
      const result = await mediator.send<SimplifyDeveloperPlansResponse>(
        new GetSimplifyDeveloperPlansQuery(params)
      );
      setData(result.items, result.totalCount);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load Simplify plans');
      setError(error);
      logger.error('useSimplifyDeveloperPlans', 'fetchData error', err);
    }
  }, [pagination, sorting, debouncedSearch, setData, setLoading, setError]);

  const extendTrial = useCallback(
    async (subscriptionId: string, extendDate: string) => {
      setExtending(true);
      setExtendError(null);
      setExtendSuccess(false);
      try {
        const result = await mediator.send<ExtendSimplifyTrialResult>(
          new ExtendSimplifyTrialFECommand(subscriptionId, extendDate)
        );
        updateItemTrialDate(result.subscriptionId, result.extendDate);
        setExtendSuccess(true);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to extend trial');
        setExtendError(error);
        logger.error('useSimplifyDeveloperPlans', 'extendTrial error', err);
      } finally {
        setExtending(false);
      }
    },
    [setExtending, setExtendError, setExtendSuccess, updateItemTrialDate]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    items,
    totalCount,
    loading,
    error,
    refetch: fetchData,
    extendTrial,
  };
}
